/**
 * Who owns the reader-entry loading overlay.
 *
 * Entering a reader raises an overlay so the seeker never watches the session
 * canvas assemble itself. Two functions can lower it, and for a while both did:
 * `handleReaderSelection` lowered it when `router.push` settled, while
 * `handleRouteChange` raised it again and lowered it only after the detail
 * fetch, `start_session` and the welcome round trip had painted the profile.
 *
 * `router.push` settles the moment the URL is confirmed. So on the community
 * "Get Reading" path — the only path through `handleReaderSelection` — the
 * overlay came down over an empty `.pane-top`, whose background is literally
 * `#000`, and stayed that way for the seconds the entry actually takes. Typing
 * the same URL never calls `handleReaderSelection`, which is why the direct
 * route looked fine.
 *
 * The rule: whoever will finish the entry owns the overlay. A navigation is
 * about to run, so the route change owns it — unless no navigation will happen,
 * in which case nothing else would ever lower it.
 *
 * Pure: it decides, the caller performs.
 */
export function liftsOverlayHere({ sameLocation = false, navFailed = false } = {}) {
  // Already on the target URL: router.push is a no-op, handleRouteChange never
  // fires, so this is the last chance to lower it.
  if (sameLocation) return true
  // The navigation was aborted or redirected away. Same story — nothing
  // downstream will run, and a stuck overlay is worse than an early one.
  if (navFailed) return true
  return false
}

/**
 * Entering a Mystic's page starts at the top: the portrait and the name in
 * view (R3.1, dev walk 2026-10-15). The page scrolls inside its own panes,
 * not the window, and a pane can keep the scroll it had before (the
 * Community directory, an earlier Mystic). Every pane the page can scroll in
 * goes back to the top, and the window too.
 */
const ENTRY_SCROLLERS = ['.reading-page', '.panel-zone', '.pane-top', '.reader-two-col']
export function resetEntryScroll(root = (typeof document !== 'undefined' ? document : null)) {
  if (!root || typeof root.querySelectorAll !== 'function') return
  for (const sel of ENTRY_SCROLLERS) {
    for (const el of root.querySelectorAll(sel)) {
      if (el.scrollTop) el.scrollTop = 0
    }
  }
  if (typeof window !== 'undefined' && window.scrollY && typeof window.scrollTo === 'function') window.scrollTo(0, 0)
}
